import { ChangeDetectionStrategy, Component, ElementRef, EventEmitter, HostBinding, Input, OnChanges, Output, SimpleChanges, inject } from '@angular/core';

/** Une planche de sprites : une bande horizontale de `frames` images de même taille. */
export interface SpriteSheet {
  src: string;
  frames: number;
  /** Taille d'une image dans la planche, en px. */
  frameWidth: number;
  frameHeight: number;
  /** Durée d'une lecture. */
  durationMs: number;
}

/**
 * Lecteur de planche de sprites (`vw-sprite-player`) : une bande horizontale
 * de `frames` images de même taille (PNG ou WebP transparent), jouée une fois
 * en `durationMs` quand `play` passe à vrai, puis `ended`. L'animation est un
 * simple CSS `steps()` sur la position du fond : aucune dépendance, rien à
 * charger de plus que l'image.
 *
 * Le parent donne la hauteur (une classe sur l'élément) ; la largeur suit le
 * rapport d'une image (`frameWidth` / `frameHeight`). Une planche d'une seule
 * image est une image fixe tenue `durationMs`, ce qui permet un repli tant
 * que la vraie planche n'existe pas. La dernière image reste affichée tant
 * que `play` est vrai.
 *
 * Mouvement réduit : rien ne bouge, la première image reste et `ended` part
 * tout de suite. Décoratif : caché au lecteur d'écran.
 * Usage : `<vw-sprite-player class="h-14" src="/assets/…/willy_wave.webp" [frames]="12"
 *            [frameWidth]="256" [frameHeight]="276" [durationMs]="1120" [play]="waving" (ended)="…" />`
 */
@Component({
  selector: 'vw-sprite-player',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './sprite-player.component.scss',
  template: `
    <div class="vw-sprite h-full w-full bg-no-repeat" data-test="sprite"
      [class.vw-sprite--playing]="playing"
      [style.background-image]="'url(' + src + ')'"
      [style.background-size]="frames * 100 + '% 100%'"
      [style.animation-duration.ms]="durationMs"
      [style.animation-timing-function]="timing"
      (animationend)="onEnd($event)"></div>
  `,
})
export class SpritePlayerComponent implements OnChanges {
  /** Adresse de la planche. */
  @Input() src = '';
  /** Nombre d'images de la bande. */
  @Input() frames = 1;
  /** Taille d'une image, en px de la planche : seul leur rapport compte à l'écran. */
  @Input() frameWidth = 1;
  @Input() frameHeight = 1;
  /** Durée d'une lecture. */
  @Input() durationMs = 1000;
  /** La planche se joue une fois quand l'entrée passe à vrai. */
  @Input() play = false;
  @Output() ended = new EventEmitter<void>();

  playing = false;
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

  @HostBinding('attr.aria-hidden') readonly ariaHidden = 'true';

  @HostBinding('style.aspect-ratio') get aspectRatio(): string {
    return `${this.frameWidth} / ${this.frameHeight}`;
  }

  /** Une image par cran ; une planche d'une image ne bouge pas. */
  get timing(): string {
    return this.frames > 1 ? `steps(${this.frames}, jump-none)` : 'step-start';
  }

  ngOnChanges(changes: SimpleChanges): void {
    const play = changes['play'];
    if (!play) return;
    if (!play.currentValue) {
      this.playing = false;
    } else if (!play.previousValue) {
      if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
        // Après la détection en cours : le parent peut réagir sans erreur de vérification.
        queueMicrotask(() => this.ended.emit());
      } else {
        // Le navigateur doit voir la bande arrêtée avant de la relancer, même
        // si `play` est repassé par faux dans la même image.
        void this.host.nativeElement.offsetWidth;
        this.playing = true;
      }
    }
  }

  onEnd(event: AnimationEvent): void {
    if (event.target === event.currentTarget && this.playing) this.ended.emit();
  }
}
